/**
 * Relay API Client — Landlord Portal
 *
 * All relay substrate endpoints under /landlord/api/relay/*
 * Uses credentials: 'include' and throws on non-ok responses.
 */

import { z } from 'zod';
import {
  RecipientHealthSchema,
  RecipientDetailResponseSchema,
  CaseBudgetSchema,
  LandlordBudgetSchema,
  PolicyBlockEventSchema,
} from '@resida/api-types/relay';

// ---------------------------------------------------------------------------
// Types (derived from @resida/api-types/relay Zod schemas)
// ---------------------------------------------------------------------------

// Derived from RecipientHealthSchema in @resida/api-types/relay
export type RecipientHealth = z.infer<typeof RecipientHealthSchema>;

// Derived from RecipientDetailResponseSchema in @resida/api-types/relay
export type RecipientDetailResponse = z.infer<typeof RecipientDetailResponseSchema>;

export interface RecipientListResponse {
  recipients: RecipientHealth[];
  total: number;
}

export type BudgetStatus = 'within' | 'soft_alert' | 'exhausted';

// Derived from CaseBudgetSchema in @resida/api-types/relay
export type CaseBudgetRow = z.infer<typeof CaseBudgetSchema>;

// Derived from LandlordBudgetSchema in @resida/api-types/relay
export type LandlordBudgetRow = z.infer<typeof LandlordBudgetSchema>;

export interface CaseBudgetListResponse {
  cases: CaseBudgetRow[];
  total: number;
}

// Derived from PolicyBlockEventSchema in @resida/api-types/relay
export type PolicyBlockEvent = z.infer<typeof PolicyBlockEventSchema>;

export interface PolicyBlockListResponse {
  events: PolicyBlockEvent[];
  total: number;
}

// ---------------------------------------------------------------------------
// Base fetch helper
// ---------------------------------------------------------------------------

async function relayFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/landlord/api/relay${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json();
      if (err.message) message = err.message;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Recipients
// ---------------------------------------------------------------------------

export async function fetchRecipients(params: {
  tier?: 'healthy' | 'degraded' | 'quarantined';
  page?: number;
  limit?: number;
} = {}): Promise<RecipientListResponse> {
  const qs = new URLSearchParams();
  if (params.tier) qs.set('tier', params.tier);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return relayFetch<RecipientListResponse>(`/recipients${query}`);
}

export async function fetchRecipientDetail(address: string): Promise<RecipientDetailResponse> {
  return relayFetch<RecipientDetailResponse>(`/recipients/${encodeURIComponent(address)}`);
}

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------

export async function fetchCaseBudgets(params: {
  page?: number;
  limit?: number;
  sort?: 'status' | 'wakeCount';
} = {}): Promise<CaseBudgetListResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.sort) qs.set('sort', params.sort);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return relayFetch<CaseBudgetListResponse>(`/budgets/cases${query}`);
}

export async function fetchLandlordBudget(): Promise<LandlordBudgetRow> {
  return relayFetch<LandlordBudgetRow>(`/budgets/landlord`);
}

export async function overrideCaseBudget(
  caseId: string,
  body: { additionalWakes: number; note: string }
): Promise<{ budget: CaseBudgetRow }> {
  return relayFetch<{ budget: CaseBudgetRow }>(
    `/budgets/cases/${encodeURIComponent(caseId)}/override`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ---------------------------------------------------------------------------
// Policy Blocks
// ---------------------------------------------------------------------------

export async function fetchPolicyBlocks(params: {
  page?: number;
  limit?: number;
} = {}): Promise<PolicyBlockListResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return relayFetch<PolicyBlockListResponse>(`/policy-blocks${query}`);
}

export async function overridePolicyBlock(
  eventId: string,
  body: { note?: string } = {}
): Promise<{ event: PolicyBlockEvent }> {
  return relayFetch<{ event: PolicyBlockEvent }>(
    `/policy-blocks/${encodeURIComponent(eventId)}/override`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
